import { useAuth } from '@skyslope/auth-react';
import type { AxiosError } from 'axios';

import LocalStorageItem from '../../../constants/LocalStorageItem';
import { useSetIntegrationEnablement } from '../../../hooks/domain/integrations/useSetIntegrationEnablement';
import useCustomSnackbar from '../../../hooks/useCustomSnackbar';
import { IntegrationKey } from '../../../resources/server/IntegrationsServer';

// Define RealSatisfied error messages
const REALSATISFIED_ERROR_MESSAGES = {
  // Existing Brokeraged agent with RealSatisified
  REAL_SATISFIED_ACCOUNT_REGISTERED_OTHER_BROKER:
    'User already registered under an existing RealSatisified brokerage account. Unable to opt-in. Please contact support@realsatisfied.com',
  // Existing RealSatisfied Agent and already Opted in for SkySlope
  REAL_SATISFIED_ACCOUNT_ALREADY_EXISTS: 'Conflict, this user has already opted-in with RealSatisfied',
  DEFAULT: 'An error occurred while updating the RealSatisfied integration',
};

// Define RealSatisfied success messages
const REALSATISFIED_SUCCESS_MESSAGES = {
  ACTIVE: 'RealSatisfied integration enabled successfully, you will receive an email from RealSatisfied shortly',
  PENDING: 'RealSatisfied integration is pending, you will receive an email from RealSatisfied shortly',
};

export interface UseRealSatisfiedOptInResult {
  /**
   * Callback function to activate RealSatisfied integration
   */
  activateIntegration: () => Promise<void>;
}

/**
 * Hook for handling RealSatisfied integration opt-in
 * @returns Object containing learnMoreUrl and activateIntegration function
 */
export const useRealSatisfiedOptIn = (): UseRealSatisfiedOptInResult => {
  const userId = useAuth()?.userContext?.id;
  const { addSuccessSnackbar, addErrorSnackbar } = useCustomSnackbar();

  // Use the enhanced hook with custom handlers
  const { mutateAsync: setIntegration } = useSetIntegrationEnablement({
    callSnackbar: false,
  });

  const activateIntegration = async (): Promise<void> => {
    try {
      const response = await setIntegration({
        integrationType: IntegrationKey.RealSatisfied,
        switchToEnablement: 'active',
      });
      if (response?.userEnablement === 'active') {
        addSuccessSnackbar(REALSATISFIED_SUCCESS_MESSAGES.ACTIVE, {
          autoHideDuration: 5000,
        });
      } else if (response?.userEnablement === 'pending') {
        addSuccessSnackbar(REALSATISFIED_SUCCESS_MESSAGES.PENDING, {
          autoHideDuration: 5000,
        });
      }
    } catch (error: unknown) {
      const errorStatus = (error as AxiosError)?.response?.data?.serviceStatus?.status;
      const message = REALSATISFIED_ERROR_MESSAGES[errorStatus as keyof typeof REALSATISFIED_ERROR_MESSAGES];
      if (message) {
        addErrorSnackbar(message, {
          autoHideDuration: 30000,
        });
      } else {
        addErrorSnackbar(REALSATISFIED_ERROR_MESSAGES.DEFAULT, {
          autoHideDuration: 30000,
        });
      }

      localStorage.removeItem(`${LocalStorageItem.RealsatisfiedModalLastDismissed}-${userId}`);
    }
  };

  return {
    activateIntegration,
  };
};
